import { TeamUserRolesEnum } from "@/enums/team-user-role";
import useAccess from "@/hooks/useAccess";
import { useNewNavigation } from "@/hooks/useNewNavigation";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { NavLink } from "react-router-dom";

type NavEntry = {
  to: string;
  label: string;
};

export function NavMain({
  className,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  const { hasAccess } = useAccess();
  const { isNewNavigationEnabled } = useNewNavigation();

  const { t } = useTranslation();

  const hasProfessionalAccess = hasAccess({
    requiredLicences: ["professional"],
  });
  const hasChatAccess = hasAccess({
    requiredLicences: ["basic", "professional"],
  });
  const hasTeamAccess = hasAccess({
    requiredRoles: [
      TeamUserRolesEnum.ADMIN,
      TeamUserRolesEnum.ADVISOR,
      TeamUserRolesEnum.MANAGER,
    ],
  });

  // The paths keep the exact shape they had before this list existed — two
  // absolute, two relative. Normalising them here would silently change how
  // React Router resolves them from a nested route.
  const prospecting: NavEntry[] =
    hasProfessionalAccess && !isNewNavigationEnabled
      ? [{ to: "/prospecting", label: t("navbar.prospecting") }]
      : [];
  const listing: NavEntry[] = hasProfessionalAccess
    ? [{ to: "listing", label: t("navbar.listing") }]
    : [];
  const chat: NavEntry[] = hasChatAccess
    ? [{ to: "/chat", label: t("navbar.chat") }]
    : [];
  const team: NavEntry[] = hasTeamAccess
    ? [{ to: "team", label: t("navbar.team") }]
    : [];

  // Every team opens on the chat, so the chat leads the menu: the first entry
  // should be the page they just landed on. Listing stays on both navigations
  // — those teams still consult the lists and company records they built from
  // the prospecting module.
  const entries: NavEntry[] = [...chat, ...prospecting, ...listing, ...team];

  return (
    <nav
      className={cn("flex items-center space-x-4 lg:space-x-6", className)}
      {...props}
    >
      {entries.map((entry) => (
        <NavLink
          key={entry.to}
          to={entry.to}
          className={({ isActive }) =>
            `text-sm font-medium ${
              !isActive && "text-muted-foreground"
            } transition-colors hover:text-primary`
          }
        >
          {entry.label}
        </NavLink>
      ))}
    </nav>
  );
}
